"use client";

import React, { useState, memo, useMemo } from "react";
import { cn } from "@/lib/utils";
import { PlotlyChart } from "./PlotlyChart";

// ============================================================================
// Types
// ============================================================================

interface MetricsSet {
  total: number;
  winning: number;
  losing: number;
  pct_profitable: number;
  gross_profit: number;
  gross_profit_pct: number;
  gross_loss: number;
  gross_loss_pct: number;
  net_profit: number;
  net_profit_pct: number;
  commission: number;
  max_contracts: number;
  avg_pnl: number;
  avg_pnl_pct: number;
  avg_win: number;
  avg_win_pct: number;
  avg_loss: number;
  avg_loss_pct: number;
  ratio: number;
  max_win: number;
  max_win_pct: number;
  max_loss: number;
  max_loss_pct: number;
  avg_bars: number;
  avg_bars_win: number;
  avg_bars_loss: number;
  max_runup?: number;
  avg_runup?: number;
  avg_runup_duration?: number;
  max_drawdown?: number;
  avg_drawdown?: number;
  avg_drawdown_duration?: number;
}

export interface BacktestPayload {
  overview: {
    total_pnl: number;
    total_pnl_pct: number;
    max_drawdown: number;
    max_drawdown_pct: number;
    total_trades: number;
    win_rate: number;
    winners: number;
    buy_hold_pnl: number;
    buy_hold_pnl_pct: number;
  };
  performance: {
    initial_capital: number;
    all: MetricsSet | null;
    long: MetricsSet | null;
    short: MetricsSet | null;
    open_pl?: { value: number; pct: number } | null;
    open_long_pl?: { value: number; pct: number } | null;
    open_short_pl?: { value: number; pct: number } | null;
  };
  trades_analysis: {
    all: MetricsSet | null;
    long: MetricsSet | null;
    short: MetricsSet | null;
    open_trades: { all: number; long: number; short: number };
  };
  risk_ratios: {
    sharpe: number;
    sortino: number;
    profit_factor: { all: number | null; long: number | null; short: number | null };
  };
  trades: Array<{
    trade_num: number;
    trade_type: "Long" | "Short";
    entry_time: string;
    exit_time: string;
    entry_price: number;
    exit_price: number;
    position_size: number;
    position_size_usd: number;
    net_pnl: number;
    net_pnl_pct: number;
    runup: number;
    runup_pct: number;
    drawdown: number;
    drawdown_pct: number;
    cumulative_pnl: number;
    cumulative_pnl_pct: number;
  }>;
  chart: {
    data: any[];
    layout: any;
  };
}

// ============================================================================
// Utility: Parse backtest payload from HTML output
// ============================================================================

export function parseBacktestPayload(output: { type: string; data: any }): BacktestPayload | null {
  if (output.type !== "html" || typeof output.data !== "string") return null;
  if (!output.data.includes('data-visualization-type="backtest-tabs"')) return null;
  
  const payloadMatch = output.data.match(/data-payload="([^"]+)"/);
  if (!payloadMatch) return null;
  
  try {
    const decoded = atob(payloadMatch[1]);
    // Handle Infinity/-Infinity/NaN which are invalid JSON but may come from Python
    const sanitized = decoded.replace(/:\s*Infinity/g, ': "__INF__"').replace(/:\s*-Infinity/g, ': "__NEG_INF__"').replace(/:\s*NaN/g, ': "__NAN__"');
    return JSON.parse(sanitized, (key, value) => {
      if (value === "__INF__") return Infinity;
      if (value === "__NEG_INF__") return -Infinity;
      if (value === "__NAN__") return NaN;
      return value;
    }) as BacktestPayload;
  } catch {
    return null;
  }
}

// ============================================================================
// Tooltips
// ============================================================================

const TOOLTIPS: Record<string, Record<string, string>> = {
  overview: {
    "Total P&L": "Final equity minus initial capital. Includes both realized (closed) and unrealized (open) P&L",
    "Max equity drawdown": "Largest peak-to-trough decline in account equity",
    "Total trades": "Number of closed trades during the backtest period",
    "Profitable trades": "Percentage of closed trades that ended in profit",
    "Buy and Hold P&L": "Return if you bought at first trade entry and held until backtest end. Different from stats 'Buy & Hold Return' which uses backtest start",
  },
  performance: {
    "Initial Capital": "Starting account balance at the beginning of the backtest",
    "Open P&L": "Unrealized P&L from positions still open",
    "Net profit": "Sum of P&L from all closed trades",
    "Gross profit": "Sum of P&L from winning trades only",
    "Gross loss": "Sum of P&L from losing trades only",
    "Commission paid": "Total commissions deducted across all closed trades",
    "Max contracts held": "Largest position size (shares/contracts) in any single trade",
    "Avg intra-trade run-up duration (days)": "Average duration from trade entry to when max unrealized profit was reached",
    "Avg intra-trade run-up": "Average of the maximum unrealized profit reached during each trade",
    "Max intra-trade run-up": "The single highest unrealized profit reached during any trade",
    "Avg intra-trade drawdown duration (days)": "Average duration from trade entry to when max unrealized loss was reached",
    "Avg intra-trade drawdown": "Average of the maximum unrealized loss experienced during each trade",
    "Max intra-trade drawdown": "The single deepest unrealized loss experienced during any trade",
  },
  trades_analysis: {
    "Total closed trades": "Number of trades that were entered and exited during the backtest",
    "Total open trades": "Number of positions still open at backtest end",
    "Winning trades": "Number of closed trades with P&L > 0",
    "Losing trades": "Number of closed trades with P&L < 0",
    "Percent profitable": "Winning trades divided by total closed trades, as a percentage",
    "Avg P&L": "Mean P&L across all closed trades",
    "Avg winning trade": "Mean P&L of winning trades only",
    "Avg losing trade": "Mean P&L of losing trades only",
    "Ratio avg win / avg loss": "Average winning trade divided by average losing trade",
    "Largest winning trade": "Highest P&L from a single winning trade",
    "Largest winning trade percent": "Highest return % from a single winning trade",
    "Largest losing trade": "Deepest loss from a single losing trade",
    "Largest losing trade percent": "Deepest loss % from a single losing trade",
    "Avg # bars in trades": "Mean number of bars between entry and exit across all trades",
    "Avg # bars in winning trades": "Mean holding period in bars for winning trades",
    "Avg # bars in losing trades": "Mean holding period in bars for losing trades",
  },
  risk_ratios: {
    "Sharpe ratio": "Risk-adjusted return based on equity curve volatility",
    "Sortino ratio": "Risk-adjusted return that only penalizes downside volatility",
    "Profit factor": "Gross profit divided by gross loss",
  },
};

// ============================================================================
// Sub-components
// ============================================================================

type TabId = "overview" | "performance" | "trades_analysis" | "risk_ratios" | "trades_list";

const TABS: { id: TabId; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "performance", label: "Performance" },
  { id: "trades_analysis", label: "Trades analysis" },
  { id: "risk_ratios", label: "Risk/performance ratios" },
  { id: "trades_list", label: "List of trades" },
];

// Tooltip icon SVG (matches Python _TIP_ICON)
const TIP_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
    <path d="M12 17h.01"/>
  </svg>
);

// Tooltip component - matches Python _tip function
function Tip({ label, tab, right = false }: { label: string; tab: string; right?: boolean }) {
  const tooltip = TOOLTIPS[tab]?.[label];
  if (!tooltip) return <>{label}</>;
  
  // Python: {label}<span class="{cls}" data-tip="{text}">{_TIP_ICON}</span>
  return (
    <>
      {label}
      <span className={cn("tip tip-icon", right && "tip-r")} data-tip={tooltip}>
        {TIP_ICON}
      </span>
    </>
  );
}

// ============================================================================
// Format Helpers (module scope for performance)
// ============================================================================

const isNullOrNaN = (v: number | null | undefined): boolean =>
  v == null || (typeof v === "number" && isNaN(v));

function fmtUsd(v: number | null | undefined): string {
  if (isNullOrNaN(v)) return "N/A";
  return `$${v!.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtPct(v: number | null | undefined): string {
  if (isNullOrNaN(v)) return "N/A";
  return `${v!.toFixed(2)}%`;
}

function fmtNum(v: number | null | undefined, decimals = 2): string {
  if (isNullOrNaN(v)) return "N/A";
  if (v === Infinity) return "∞";
  return v!.toFixed(decimals);
}

// fmtSigned: with +/- sign (for Net profit, Open P&L)
function fmtSigned(v: number | null | undefined, isPct = false): string {
  if (isNullOrNaN(v)) return "N/A";
  const sign = v! >= 0 ? "+" : "";
  return isPct ? `${sign}${v!.toFixed(2)}%` : `${sign}${v!.toFixed(2)} USD`;
}

// fmtPlain: no sign prefix (for Gross profit/loss, Avg winning/losing)
function fmtPlain(v: number | null | undefined, isPct = false): string {
  if (isNullOrNaN(v)) return "N/A";
  return isPct ? `${v!.toFixed(2)}%` : `${v!.toFixed(2)} USD`;
}

// Dual line with +/- sign (Net profit, Open P&L)
function fmtSignedDualLine(usd: number | null | undefined, pct: number | null | undefined): string {
  return `${fmtSigned(usd)}<br>${fmtSigned(pct, true)}`;
}

// Dual line without sign (Gross profit/loss, Avg winning/losing)
function fmtPlainDualLine(usd: number | null | undefined, pct: number | null | undefined): string {
  return `${fmtPlain(usd)}<br>${fmtPlain(pct, true)}`;
}

// Format as "X.XX USD" (Python format)
function fmtUsdSuffix(v: number | null | undefined): string {
  if (isNullOrNaN(v)) return "N/A";
  return `${v!.toFixed(2)} USD`;
}

// Format with comma separators (for trades list)
function fmtComma(v: number | null | undefined, decimals = 2): string {
  if (v == null) return "N/A";
  return v.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

// Format percentage with sign (for trades list)
function fmtPctSigned(v: number | null | undefined): string {
  if (v == null) return "N/A";
  return `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;
}

// Format percentage plain (for trades list)
function fmtPctPlain(v: number | null | undefined): string {
  if (v == null) return "N/A";
  return `${v.toFixed(2)}%`;
}

// Months array for date formatting
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Format datetime: "2025-10-30 13:45 ET" → "Oct 30, 2025, 13:45 ET"
function fmtDateTime(dt: string | null | undefined): string {
  if (!dt) return "N/A";
  
  // Single regex to extract all parts: YYYY-MM-DD HH:MM [TZ]
  const match = dt.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})(?:\s+([A-Z]{2,4}))?/);
  if (!match) return dt;
  
  const [, year, month, day, hours, mins, tz] = match;
  const mon = MONTHS[parseInt(month) - 1];
  return `${mon} ${day}, ${year}, ${hours}:${mins}${tz ? ` ${tz}` : ""}`;
}

// Profit factor formatters
function fmtProfitFactor(v: number | null): string {
  if (v === null || (typeof v === "number" && isNaN(v))) return "N/A";
  if (v === Infinity) return "∞";
  return v.toFixed(3);
}

function fmtProfitFactorLongShort(v: number | null): string {
  if (v === null) return "";
  if (v === Infinity) return "∞";
  if (typeof v === "number" && isNaN(v)) return "";
  return v.toFixed(3);
}

// Check if open P&L has actual data (not just 0,0)
function hasOpenPl(pl?: { value: number; pct: number } | null): boolean {
  if (!pl) return false;
  return pl.value !== 0 || pl.pct !== 0;
}

// Overview tab with chart and key metrics
function OverviewTab({ data }: { data: BacktestPayload }) {
  const { overview, chart } = data;
  const pnlColor = overview.total_pnl >= 0 ? "text-[#2ECC71]" : "text-[#E74C3C]";
  const bhColor = overview.buy_hold_pnl >= 0 ? "text-[#4A90E2]" : "text-[#E74C3C]";
  
  return (
    <div>
      {/* Metrics summary bar */}
      <div className="flex" style={{ margin: 0, padding: '0 20px', justifyContent: 'space-evenly' }}>
        {/* Total P&L */}
        <div className="flex flex-col gap-1.5 flex-1">
          <div className="metric-label" style={{ fontSize: '11px', color: '#bbb', fontWeight: 600, letterSpacing: '0.2px' }}>
            <Tip label="Total P&L" tab="overview" />
          </div>
          <div style={{ fontSize: '18px', fontWeight: 700 }} className={pnlColor}>
            {fmtUsd(overview.total_pnl)}{" "}
            <span style={{ fontSize: '12px', color: '#9aa', fontWeight: 600 }}>{fmtPct(overview.total_pnl_pct)}</span>
          </div>
        </div>
        
        {/* Max equity drawdown */}
        <div className="flex flex-col gap-1.5 flex-1">
          <div className="metric-label" style={{ fontSize: '11px', color: '#bbb', fontWeight: 600, letterSpacing: '0.2px' }}>
            <Tip label="Max equity drawdown" tab="overview" />
          </div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#E74C3C' }}>
            {fmtUsd(overview.max_drawdown)}{" "}
            <span style={{ fontSize: '12px', color: '#9aa', fontWeight: 600 }}>{fmtPct(overview.max_drawdown_pct)}</span>
          </div>
        </div>
        
        {/* Total trades */}
        <div className="flex flex-col gap-1.5 flex-1">
          <div className="metric-label" style={{ fontSize: '11px', color: '#bbb', fontWeight: 600, letterSpacing: '0.2px' }}>
            <Tip label="Total trades" tab="overview" />
          </div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#e5e5e5' }}>
            {overview.total_trades ?? 'N/A'}
          </div>
        </div>
        
        {/* Profitable trades */}
        <div className="flex flex-col gap-1.5 flex-1">
          <div className="metric-label" style={{ fontSize: '11px', color: '#bbb', fontWeight: 600, letterSpacing: '0.2px' }}>
            <Tip label="Profitable trades" tab="overview" right />
          </div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#e5e5e5' }}>
            {fmtPct(overview.win_rate)}{" "}
            <span style={{ fontSize: '12px', color: '#9aa', fontWeight: 600 }}>{overview.winners ?? 'N/A'}/{overview.total_trades ?? 'N/A'}</span>
          </div>
        </div>
        
        {/* Buy and Hold P&L */}
        <div className="flex flex-col gap-1.5 flex-1">
          <div className="metric-label" style={{ fontSize: '11px', color: '#bbb', fontWeight: 600, letterSpacing: '0.2px' }}>
            <Tip label="Buy and Hold P&L" tab="overview" right />
          </div>
          <div style={{ fontSize: '18px', fontWeight: 700 }} className={bhColor}>
            {fmtUsd(overview.buy_hold_pnl)}{" "}
            <span style={{ fontSize: '12px', color: '#9aa', fontWeight: 600 }}>{fmtPct(overview.buy_hold_pnl_pct)}</span>
          </div>
        </div>
      </div>
      
      {/* Chart */}
      {chart && chart.data && (
        <div className="mt-2">
          <PlotlyChart data={{ data: chart.data, layout: chart.layout }} noBorder />
        </div>
      )}
    </div>
  );
}

// Three-column metrics table (ALL / LONG / SHORT)
function ThreeColumnTable({ 
  rows, 
  tab 
}: { 
  rows: Array<{ label: string; all: string; long: string; short: string }>;
  tab: string;
}) {
  return (
    <table className="trades-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', tableLayout: 'fixed' }}>
      <thead style={{ background: '#2a2a2a' }}>
        <tr>
          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#999', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', wordWrap: 'break-word', whiteSpace: 'normal' }}>METRIC</th>
          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#999', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', wordWrap: 'break-word', whiteSpace: 'normal' }}>ALL</th>
          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#999', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', wordWrap: 'break-word', whiteSpace: 'normal' }}>LONG</th>
          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#999', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', wordWrap: 'break-word', whiteSpace: 'normal' }}>SHORT</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="hover:bg-[#1a1a1a]">
            <td style={{ padding: '12px 16px', borderBottom: '1px solid #222', color: '#e5e5e5', wordWrap: 'break-word', whiteSpace: 'normal' }}>
              <Tip label={row.label} tab={tab} />
            </td>
            <td style={{ padding: '12px 16px', borderBottom: '1px solid #222', color: '#e5e5e5', wordWrap: 'break-word', whiteSpace: 'normal' }} dangerouslySetInnerHTML={{ __html: row.all }} />
            <td style={{ padding: '12px 16px', borderBottom: '1px solid #222', color: '#e5e5e5', wordWrap: 'break-word', whiteSpace: 'normal' }} dangerouslySetInnerHTML={{ __html: row.long }} />
            <td style={{ padding: '12px 16px', borderBottom: '1px solid #222', color: '#e5e5e5', wordWrap: 'break-word', whiteSpace: 'normal' }} dangerouslySetInnerHTML={{ __html: row.short }} />
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Performance tab
function PerformanceTab({ data }: { data: BacktestPayload }) {
  const { performance } = data;
  const all = performance.all;
  const long = performance.long;
  const short = performance.short;
  
  const rows = [
    { label: "Initial Capital", all: fmtUsdSuffix(performance.initial_capital), long: "", short: "" },
    { 
      label: "Open P&L", 
      // ALL: "N/A" when 0,0; LONG/SHORT: "" (empty) when 0,0
      all: hasOpenPl(performance.open_pl) ? fmtSignedDualLine(performance.open_pl?.value, performance.open_pl?.pct) : "N/A",
      long: hasOpenPl(performance.open_long_pl) ? fmtSignedDualLine(performance.open_long_pl?.value, performance.open_long_pl?.pct) : "",
      short: hasOpenPl(performance.open_short_pl) ? fmtSignedDualLine(performance.open_short_pl?.value, performance.open_short_pl?.pct) : "",
    },
    { label: "Net profit", all: fmtSignedDualLine(all?.net_profit, all?.net_profit_pct), long: fmtSignedDualLine(long?.net_profit, long?.net_profit_pct), short: fmtSignedDualLine(short?.net_profit, short?.net_profit_pct) },
    { label: "Gross profit", all: fmtPlainDualLine(all?.gross_profit, all?.gross_profit_pct), long: fmtPlainDualLine(long?.gross_profit, long?.gross_profit_pct), short: fmtPlainDualLine(short?.gross_profit, short?.gross_profit_pct) },
    { label: "Gross loss", all: fmtPlainDualLine(all?.gross_loss, all?.gross_loss_pct), long: fmtPlainDualLine(long?.gross_loss, long?.gross_loss_pct), short: fmtPlainDualLine(short?.gross_loss, short?.gross_loss_pct) },
    { label: "Commission paid", all: fmtUsdSuffix(all?.commission), long: fmtUsdSuffix(long?.commission), short: fmtUsdSuffix(short?.commission) },
    { label: "Max contracts held", all: fmtNum(all?.max_contracts, 0), long: fmtNum(long?.max_contracts, 0), short: fmtNum(short?.max_contracts, 0) },
    { label: "Avg intra-trade run-up duration (days)", all: fmtNum(all?.avg_runup_duration, 1), long: fmtNum(long?.avg_runup_duration, 1), short: fmtNum(short?.avg_runup_duration, 1) },
    { label: "Avg intra-trade run-up", all: fmtUsdSuffix(all?.avg_runup), long: fmtUsdSuffix(long?.avg_runup), short: fmtUsdSuffix(short?.avg_runup) },
    { label: "Max intra-trade run-up", all: fmtUsdSuffix(all?.max_runup), long: fmtUsdSuffix(long?.max_runup), short: fmtUsdSuffix(short?.max_runup) },
    { label: "Avg intra-trade drawdown duration (days)", all: fmtNum(all?.avg_drawdown_duration, 1), long: fmtNum(long?.avg_drawdown_duration, 1), short: fmtNum(short?.avg_drawdown_duration, 1) },
    { label: "Avg intra-trade drawdown", all: fmtUsdSuffix(all?.avg_drawdown), long: fmtUsdSuffix(long?.avg_drawdown), short: fmtUsdSuffix(short?.avg_drawdown) },
    { label: "Max intra-trade drawdown", all: fmtUsdSuffix(all?.max_drawdown), long: fmtUsdSuffix(long?.max_drawdown), short: fmtUsdSuffix(short?.max_drawdown) },
  ];
  
  return <ThreeColumnTable rows={rows} tab="performance" />;
}

// Trades Analysis tab
function TradesAnalysisTab({ data }: { data: BacktestPayload }) {
  const { trades_analysis } = data;
  const all = trades_analysis.all;
  const long = trades_analysis.long;
  const short = trades_analysis.short;
  const open = trades_analysis.open_trades;
  
  const rows = [
    { label: "Total open trades", all: String(open?.all ?? 0), long: String(open?.long ?? 0), short: String(open?.short ?? 0) },
    { label: "Total closed trades", all: fmtNum(all?.total, 0), long: fmtNum(long?.total, 0), short: fmtNum(short?.total, 0) },
    { label: "Winning trades", all: fmtNum(all?.winning, 0), long: fmtNum(long?.winning, 0), short: fmtNum(short?.winning, 0) },
    { label: "Losing trades", all: fmtNum(all?.losing, 0), long: fmtNum(long?.losing, 0), short: fmtNum(short?.losing, 0) },
    { label: "Percent profitable", all: fmtPct(all?.pct_profitable), long: fmtPct(long?.pct_profitable), short: fmtPct(short?.pct_profitable) },
    { label: "Avg P&L", all: fmtPlainDualLine(all?.avg_pnl, all?.avg_pnl_pct), long: fmtPlainDualLine(long?.avg_pnl, long?.avg_pnl_pct), short: fmtPlainDualLine(short?.avg_pnl, short?.avg_pnl_pct) },
    { label: "Avg winning trade", all: fmtPlainDualLine(all?.avg_win, all?.avg_win_pct), long: fmtPlainDualLine(long?.avg_win, long?.avg_win_pct), short: fmtPlainDualLine(short?.avg_win, short?.avg_win_pct) },
    { label: "Avg losing trade", all: fmtPlainDualLine(all?.avg_loss, all?.avg_loss_pct), long: fmtPlainDualLine(long?.avg_loss, long?.avg_loss_pct), short: fmtPlainDualLine(short?.avg_loss, short?.avg_loss_pct) },
    { label: "Ratio avg win / avg loss", all: fmtNum(all?.ratio, 3), long: fmtNum(long?.ratio, 3), short: fmtNum(short?.ratio, 3) },
    { label: "Largest winning trade", all: fmtUsdSuffix(all?.max_win), long: fmtUsdSuffix(long?.max_win), short: fmtUsdSuffix(short?.max_win) },
    { label: "Largest winning trade percent", all: fmtPct(all?.max_win_pct), long: fmtPct(long?.max_win_pct), short: fmtPct(short?.max_win_pct) },
    { label: "Largest losing trade", all: fmtUsdSuffix(all?.max_loss), long: fmtUsdSuffix(long?.max_loss), short: fmtUsdSuffix(short?.max_loss) },
    { label: "Largest losing trade percent", all: fmtPct(all?.max_loss_pct), long: fmtPct(long?.max_loss_pct), short: fmtPct(short?.max_loss_pct) },
    { label: "Avg # bars in trades", all: fmtNum(all?.avg_bars, 0), long: fmtNum(long?.avg_bars, 0), short: fmtNum(short?.avg_bars, 0) },
    { label: "Avg # bars in winning trades", all: fmtNum(all?.avg_bars_win, 0), long: fmtNum(long?.avg_bars_win, 0), short: fmtNum(short?.avg_bars_win, 0) },
    { label: "Avg # bars in losing trades", all: fmtNum(all?.avg_bars_loss, 0), long: fmtNum(long?.avg_bars_loss, 0), short: fmtNum(short?.avg_bars_loss, 0) },
  ];
  
  return <ThreeColumnTable rows={rows} tab="trades_analysis" />;
}

// Risk Ratios tab
function RiskRatiosTab({ data }: { data: BacktestPayload }) {
  const { risk_ratios } = data;
  const pf = risk_ratios.profit_factor;
  
  const rows = [
    { label: "Sharpe ratio", all: fmtNum(risk_ratios.sharpe, 3), long: "", short: "" },
    { label: "Sortino ratio", all: fmtNum(risk_ratios.sortino, 3), long: "", short: "" },
    { label: "Profit factor", all: fmtProfitFactor(pf?.all), long: fmtProfitFactorLongShort(pf?.long), short: fmtProfitFactorLongShort(pf?.short) },
  ];
  
  return <ThreeColumnTable rows={rows} tab="risk_ratios" />;
}

// ============================================================================
// Style Constants (module scope for performance)
// ============================================================================

const TABLE_TH_STYLE: React.CSSProperties = { 
  padding: '12px 16px', 
  textAlign: 'left', 
  fontWeight: 600, 
  color: '#999', 
  fontSize: '11px', 
  textTransform: 'uppercase', 
  letterSpacing: '0.5px', 
  wordWrap: 'break-word', 
  whiteSpace: 'normal' 
};

const TABLE_TD_STYLE: React.CSSProperties = { 
  padding: '12px 16px', 
  borderBottom: '1px solid #222', 
  color: '#e5e5e5', 
  wordWrap: 'break-word', 
  whiteSpace: 'normal' 
};

const METRIC_CELL_STYLE: React.CSSProperties = { 
  ...TABLE_TD_STYLE, 
  verticalAlign: 'middle', 
  textAlign: 'center' 
};

const TRADE_NUM_STYLE: React.CSSProperties = { 
  ...TABLE_TD_STYLE, 
  fontWeight: 600, 
  borderRight: '1px solid #333', 
  verticalAlign: 'middle' 
};

// Trades list table with entry/exit rows
function TradesListTable({ trades }: { trades: BacktestPayload["trades"] }) {
  if (!trades || trades.length === 0) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#666', fontSize: '14px' }}>No closed trades to display</div>;
  }
  
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="trades-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', tableLayout: 'fixed' }}>
        <thead style={{ background: '#2a2a2a' }}>
          <tr>
            <th style={TABLE_TH_STYLE}>Trade #</th>
            <th style={TABLE_TH_STYLE}>Type</th>
            <th style={TABLE_TH_STYLE}>Date/Time</th>
            <th style={TABLE_TH_STYLE}>Price</th>
            <th style={TABLE_TH_STYLE}>Position size</th>
            <th style={TABLE_TH_STYLE}>Net P&L</th>
            <th style={TABLE_TH_STYLE}>Run-up</th>
            <th style={TABLE_TH_STYLE}>Drawdown</th>
            <th style={TABLE_TH_STYLE}>Cumulative P&L</th>
          </tr>
        </thead>
        <tbody>
          {trades.map((trade) => {
            const pnlColor = (trade.net_pnl ?? 0) >= 0 ? "#22c55e" : "#ef4444";
            const cumColor = (trade.cumulative_pnl ?? 0) >= 0 ? "#22c55e" : "#ef4444";
            const typeColor = trade.trade_type === "Long" ? "#22c55e" : "#ef4444";
            
            return (
              <React.Fragment key={trade.trade_num}>
                {/* Entry row - trade-group class */}
                <tr className="trade-group" style={{ borderTop: '2px solid #333' }}>
                  <td rowSpan={2} style={TRADE_NUM_STYLE}>
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>{trade.trade_num}</div>
                    <div style={{ fontSize: '11px', color: typeColor }}>{trade.trade_type}</div>
                  </td>
                  <td style={TABLE_TD_STYLE}>Entry</td>
                  <td style={TABLE_TD_STYLE}>{fmtDateTime(trade.entry_time)}</td>
                  <td style={TABLE_TD_STYLE}>
                    ${fmtComma(trade.entry_price)} <span style={{ fontSize: '10px', color: '#999' }}>USD</span>
                  </td>
                  <td rowSpan={2} style={METRIC_CELL_STYLE}>
                    <div>{fmtComma(trade.position_size, 0)}</div>
                    <div style={{ fontSize: '10px', color: '#999' }}>${fmtComma(trade.position_size_usd)} <span style={{ fontSize: '9px' }}>USD</span></div>
                  </td>
                  <td rowSpan={2} style={METRIC_CELL_STYLE}>
                    <div style={{ color: pnlColor, fontWeight: 600 }}>
                      ${fmtComma(trade.net_pnl)} <span style={{ fontSize: '10px' }}>USD</span>
                    </div>
                    <div style={{ fontSize: '10px', color: pnlColor }}>{fmtPctSigned(trade.net_pnl_pct)}</div>
                  </td>
                  <td rowSpan={2} style={METRIC_CELL_STYLE}>
                    <div>${fmtComma(trade.runup)} <span style={{ fontSize: '10px', color: '#999' }}>USD</span></div>
                    <div style={{ fontSize: '10px', color: '#999' }}>{fmtPctPlain(trade.runup_pct)}</div>
                  </td>
                  <td rowSpan={2} style={METRIC_CELL_STYLE}>
                    <div>${fmtComma(trade.drawdown)} <span style={{ fontSize: '10px', color: '#999' }}>USD</span></div>
                    <div style={{ fontSize: '10px', color: '#999' }}>{fmtPctPlain(trade.drawdown_pct)}</div>
                  </td>
                  <td rowSpan={2} style={METRIC_CELL_STYLE}>
                    <div style={{ color: cumColor, fontWeight: 600 }}>
                      ${fmtComma(trade.cumulative_pnl)} <span style={{ fontSize: '10px' }}>USD</span>
                    </div>
                    <div style={{ fontSize: '10px', color: cumColor }}>{fmtPctPlain(trade.cumulative_pnl_pct)}</div>
                  </td>
                </tr>
                {/* Exit row - trade-group-exit class */}
                <tr className="trade-group-exit hover:bg-[#1a1a1a]">
                  <td style={TABLE_TD_STYLE}>Exit</td>
                  <td style={TABLE_TD_STYLE}>{fmtDateTime(trade.exit_time)}</td>
                  <td style={TABLE_TD_STYLE}>
                    ${fmtComma(trade.exit_price)} <span style={{ fontSize: '10px', color: '#999' }}>USD</span>
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

interface BacktestTabsProps {
  data: BacktestPayload;
}

function BacktestTabsComponent({ data }: BacktestTabsProps) {
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  
  // Only show trades list tab if there are trades
  const visibleTabs = useMemo(() => {
    if (!data.trades || data.trades.length === 0) {
      return TABS.filter(t => t.id !== "trades_list");
    }
    return TABS;
  }, [data.trades]);
  
  const tabContent = useMemo(() => {
    switch (activeTab) {
      case "overview":
        return <OverviewTab data={data} />;
      case "performance":
        return <PerformanceTab data={data} />;
      case "trades_analysis":
        return <TradesAnalysisTab data={data} />;
      case "risk_ratios":
        return <RiskRatiosTab data={data} />;
      case "trades_list":
        return <TradesListTable trades={data.trades} />;
      default:
        return null;
    }
  }, [activeTab, data]);
  
  return (
    <div className="backtest-tabs" style={{ background: '#09090B', color: '#e5e5e5', fontFamily: "'Geist', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", margin: 0, padding: 0 }}>
      {/* CSS styles matching Python HTML_CSS_STYLES */}
      <style>{`
        .backtest-tabs .tip {
          position: relative;
          display: inline-flex;
          align-items: center;
          margin-left: 6px;
          color: #71717a;
          cursor: help;
          opacity: 0;
          transition: opacity 0.15s;
        }
        .backtest-tabs .tip svg { display: block; }
        .backtest-tabs .tip::after {
          content: attr(data-tip);
          position: absolute;
          left: 0;
          bottom: 100%;
          background: #27272a;
          color: #e5e5e5;
          padding: 8px 12px;
          border-radius: 6px;
          font-family: inherit;
          font-size: 12px;
          font-weight: 400;
          white-space: nowrap;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.15s;
          z-index: 100;
          margin-bottom: 6px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        }
        .backtest-tabs .tip:hover::after { opacity: 1; }
        .backtest-tabs tr:hover .tip { opacity: 1; }
        .backtest-tabs .metric-label .tip { opacity: 1; }
        .backtest-tabs .tip-r::after { left: auto; right: 0; }
        .backtest-tabs tr:hover .tip-icon { opacity: 1; }
        .backtest-tabs .metric-label .tip-icon { opacity: 1; }
        .backtest-tabs .trades-table tr:hover { background: #1a1a1a; }
        .backtest-tabs .tab-border::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 2px;
          height: 14px;
          background-color: transparent;
          transition: all 0.2s ease;
        }
        .backtest-tabs .tab-border:hover::before { background-color: #3a3a3a; }
        .backtest-tabs .tab-border.active::before { background-color: #fff; }
      `}</style>
      
      {/* Header */}
      <div className="header" style={{ textAlign: 'left', marginBottom: '10px', padding: 0 }}>
        {/* Title */}
        <h2 className="title" style={{ fontSize: '29px', fontWeight: 600, color: '#fff', marginBottom: '10px', textAlign: 'center' }}>Performance Analysis</h2>
        
        {/* Tab Navigation - tabs-border */}
        <div className="tabs-border" style={{ display: 'flex', gap: '0px', justifyContent: 'flex-start', borderBottom: '1px solid #333' }}>
          {visibleTabs.map((tab) => (
            <div
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn("tab-border", activeTab === tab.id && "active")}
              style={{
                padding: '10px 24px',
                fontSize: '14px',
                fontWeight: activeTab === tab.id ? 600 : 500,
                color: activeTab === tab.id ? '#fff' : '#666',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                position: 'relative',
              }}
            >
              {tab.label}
            </div>
          ))}
        </div>
      </div>
      
      {/* Tab Content */}
      <div className="tab-content active" style={{ display: 'block', padding: 0 }}>
        {tabContent}
      </div>
    </div>
  );
}

export const BacktestTabs = memo(BacktestTabsComponent);
